// src/data/chargebacks.js
// Chargeback = customer disconnected within 90 days of their activation date.
// Sentinel/placeholder dates (1900-01-01, etc.) do not count.

export const CHARGEBACK_WINDOW_DAYS = 90;

// Known sentinel/placeholder dates produced by CSV/Excel exports for "empty".
// Stored as yyyy-MM-dd; we normalize incoming values before checking.
const BAD_DATES = new Set([
  '1900-01-01', '1900-01-02', '1901-01-01',
  '2000-01-01', '2000-01-02', '2001-01-01',
]);

// Normalize a date-ish value into yyyy-MM-dd, accepting:
//   - yyyy-MM-dd (or anything starting with it)
//   - M/D/YY or M/D/YYYY (Excel/CSV default display)
//   - anything else Date() can parse
export function normalizeDate(d) {
  if (!d) return null;
  const raw = String(d).trim();
  if (!raw) return null;

  // Already yyyy-MM-dd
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  // M/D/YY or MM/DD/YYYY
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slash) {
    let [, m, day, y] = slash;
    if (y.length === 2) {
      // Excel's 2-digit year rule: 00-29 -> 2000s, 30-99 -> 1900s
      const n = parseInt(y, 10);
      y = (n <= 29 ? 2000 + n : 1900 + n).toString();
    }
    return `${y}-${m.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Fallback: let Date parse it and reformat
  const ms = new Date(raw).getTime();
  if (isNaN(ms)) return null;
  const dt = new Date(ms);
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function cleanDate(d) {
  const s = normalizeDate(d);
  if (!s) return null;
  if (BAD_DATES.has(s)) return null;
  // Any date in 1900s or before 2020 is treated as a placeholder.
  if (s < '2020-01-01') return null;
  const ms = new Date(s).getTime();
  if (isNaN(ms)) return null;
  return { s, ms };
}

// True if this order is a chargeback: real activation date, real disconnect date,
// disconnect within 90 days of activation. No activation = never paid = no chargeback.
export function isChargeback(order) {
  if (!order || !order.disconnectDate) return false;
  const disc = cleanDate(order.disconnectDate);
  const act  = cleanDate(order.date);
  if (!disc || !act) return false;
  const days = (disc.ms - act.ms) / 86400000;
  return days > 0 && days <= CHARGEBACK_WINDOW_DAYS;
}

// Days the customer stayed active before disconnecting (null if not applicable).
export function daysActive(order) {
  const disc = cleanDate(order?.disconnectDate);
  const act  = cleanDate(order?.date);
  if (!disc || !act) return null;
  return Math.max(0, Math.round((disc.ms - act.ms) / 86400000));
}

// "Canonical" id for a user record: prefer auth uid (which is what
// order.commissions[].repId uses), fall back to Firestore doc id.
export function repKey(rep) {
  return rep?.uid || rep?.id || null;
}

// Build map of managerKey -> [child rep keys] for tree traversal.
function buildDownlineMap(reps) {
  const map = {};
  reps.forEach(r => {
    if (!r.managerId) return;
    (map[r.managerId] = map[r.managerId] || []).push(repKey(r));
  });
  return map;
}

// Set of rep keys in this manager's downline (transitive, includes self).
export function getDownlineKeys(managerKey, reps) {
  const map = buildDownlineMap(reps);
  const result = new Set([managerKey]);
  const queue = [managerKey];
  while (queue.length) {
    const id = queue.shift();
    (map[id] || []).forEach(childKey => {
      if (childKey && !result.has(childKey)) {
        result.add(childKey);
        queue.push(childKey);
      }
    });
  }
  return result;
}

// Determine the chargeback view scope for a logged-in user.
// Returns { scope: 'admin'|'manager'|'rep', visibleRepKeys: Set|null }
// visibleRepKeys === null means "see everything" (admin only).
export function getChargebackScope(currentUid, userProfile, reps) {
  if (userProfile?.role === 'admin') {
    return { scope: 'admin', visibleRepKeys: null };
  }
  const downline = getDownlineKeys(currentUid, reps);
  if (downline.size > 1) {
    return { scope: 'manager', visibleRepKeys: downline };
  }
  return { scope: 'rep', visibleRepKeys: new Set([currentUid]) };
}

// For a given order, return the commission amount earned by repKey (0 if none).
export function repCommissionOnOrder(order, repKeyId) {
  if (!order?.commissions) return 0;
  const c = order.commissions.find(x => x.repId === repKeyId);
  return c?.amount || 0;
}
